/**
 * Common types
 *
 * @module
 */

// Explicitly defined constants are used because of Terser's inability to munge
// Typescript-generated enums
export const DepID_ApplicationEventsBeacon = 0
export const DepID_ClientDownloadSessionConfiguration = 1
export const DepID_ClientDownloadSessionConfigurationMessage = 2
export const DepID_ClientInitialization = 3
export const DepID_ClientInitialApplicationEventsBeacon = 4
export const DepID_ClientInitialSessionConfigSourceReceived = 5
export const DepID_ClientScheduleApplicationEventsBeaconInterval = 6
export const DepID_ClientScheduleMeasurementSchedulerRunInterval = 7
export const DepID_ClientScheduleSessionConfigurationRefreshInterval = 8
export const DepID_ClientSubsequentSessionConfigSourceReceived = 9
export const DepID_IntervalCalculator = 10
export const DepID_MeasurementSchedulerRun = 11
export const DepID_OpenInsightsFetch = 12
export const DepID_OpenInsightsPerformanceEntryManager = 13
export const DepID_PlatformTestObjectBeforeBeacon = 14
export const DepID_PlatformTestObjectBeforeTest = 15
export const DepID_PlatformTestObjectOnError = 16
export const DepID_PlatformTestObjectSendDopplerBeacon = 17
export const DepID_PlatformTestObjectSendPulsarBeacon = 18
export const DepID_RandomStringCharacterSelection = 19
export const DepID_SchedulerInitializePopulation = 20
export const DepID_SchedulerOutputDiagnosticsMessage = 21
export const DepID_SchedulerScheduleNextMeasurement = 22
export const DepID_SchedulerScheduleNextMeasurementSoon = 23
export const DepID_SchedulerUpdatePopulation = 24
export const DepID_SelectWeightedRandomSample = 25

export type EpochDelta = number
export type SemVerComponents = [string, string, string]
export type ClientVersion = SemVerComponents

export type DeviceSpec = {
  type?: string
  vendor?: string
  model?: string
}

export type BrowserSpec = {
  name?: string
  version?: string
}

export type OSSpec = {
  name?: string
  version?: string
}

export type UAParserDevice = {
  type?: string
  vendor?: string
  model?: string
}

export type UAParserBrowser = {
  name?: string
  version?: string
}

export type UAParserOS = {
  name?: string
  version?: string
}

export type BrowserMetadata = {
  userAgent: string
  deviceCategory: string
  device: UAParserDevice
  browser: UAParserBrowser
  os: UAParserOS
}

/**
 * Local page overrides, mostly for developer use but also available for page owners
 * to use. Please update the
 * [wiki page](https://github.com/cbsinteractive/doppler.js/wiki/Local-Page-Settings)
 * with any changes to these properties.
 */
export type LocalPageSettings = {
  /**
   * The amount of time (in minutes) between application event beacons
   */
  applicationEventsBeaconInterval?: number
  /**
   * The URL used to send application event reports to.
   */
  applicationLoggingEndpoint?: string
  /**
   * If set, one of:
   *
   *  - off
   *  - error
   *  - warn
   *  - info
   *  - debug
   */
  consoleLogLevel?: string
  /**
   * The amount of time (in milliseconds) to wait before downloading session config.
   */
  sessionConfigDelay?: number
  /**
   * The amount of time (in seconds) to wait after receiving the session
   * configuration before beginning measurements.
   */
  measurementStartDelay?: number
  /**
   * If given, this is the amount of time (in minutes) to wait until
   * downloading a fresh session configuration.
   */
  sessionRefreshInterval?: number
  /**
   * If given, this overrides the endpoint to which Doppler.js sends beacons.
   */
  dopplerBeaconEndpoint?: string
  /**
   * Override the time to wait between measurement scheduler checks.
   */
  schedulerRunInterval?: number
  /**
   * An origin (i.e. scheme, domain and port) used to derive the URL for session
   * configuration downloads. A good use case is developers simulating platform
   * configuration changes.
   */
  sessionConfigOrigin?: string
}

export type SiteOwnerSettingsSource = {
  /**
   * The time to wait (in minutes) between application event beacons.
   */
  application_events_interval?: number
  /**
   * The number of test objects to measure when Doppler.js first loads.
   */
  max_initial_test_objects: number
  /**
   * The amount of time (in seconds) to wait after receiving the session
   * configuration before beginning measurements.
   */
  measurement_start_delay: number
  /**
   * The time to wait (in seconds) between measurement scheduler checks.
   */
  scheduler_run_interval: number
  /**
   * The time to wait (in minutes) between session config downloads.
   */
  session_refresh_interval: number
  /**
   * Settings applying to all measurement-taking.
   */
  measurements?: MeasurementsConfigSource
}

export type AllowStrings = {
  /**
   * A list of strings to be evaluated as regular expressions. These are
   * matched against some value at runtime. If any match, then the subject
   * feature of the check _may be_ enabled, subject to any
   * {@link DisallowStrings} rules, which take precedence.
   * In other words, this is a form of whitelist.
   */
  allow?: string[]
}

export type DisallowStrings = {
  /**
   * A list of strings to be evaluated as regular expressions. These are
   * matched against some value at runtime. If any match, then the subject
   * feature of the check should be disabled. In other words, this is a
   * form of blacklist.
   */
  disallow?: string[]
}

/**
 * Same general usage as for {@link AllowStrings}
 */
export type AllowNumbers = {
  allow?: number[]
}

/**
 * Same general usage as for {@link DisallowStrings}
 */
export type DisallowNumbers = {
  disallow?: number[]
}

export type TargetFrequencyMapping = {
  /**
   * Simple mapping of a string, such as a ISO 3166-1 alpha-2 country code or a
   * continent code, to target frequency.
   */
  target_frequency?: Record<string, number>
}

export type GeoSource = {
  asn?: AllowNumbers & DisallowNumbers
  continent?: TargetFrequencyMapping
  country?: AllowStrings & DisallowStrings & TargetFrequencyMapping
}

export type PulsarMetricString = "ttlb"

export type PulsarMetrics = {
  pulsar: PulsarMetricString[]
}

export type ProfileTestObjectSource = {
  path: string
  size: number
  /**
   * Number of seconds to wait before considering a test object download to
   * have timed out
   */
  timeout: number
  target_frequency: number
  geo?: GeoSource
  metrics?: PulsarMetrics
}

export type PlatformFeaturesSource = {
  hdx?: boolean
}

export type PlatformProfileSource = {
  cdn_response_header?: string
  features?: PlatformFeaturesSource
  objects: Array<ProfileTestObjectSource>
}

export type PlatformDopplerSource = {
  id: string
}

export type PlatformPulsarSource = {
  app_id: string
  job_id: string
}

export type PlatformTestObjectOverridesSource = {
  size: number
  target_frequency?: number
}

export type PlatformSource = {
  doppler: PlatformDopplerSource
  pulsar?: PlatformPulsarSource
  hostname: string
  profile: string
  cdn_response_header?: string
  geo?: GeoSource
  objects?: PlatformTestObjectOverridesSource[]
}

export type DopplerEndpointsSource = {
  resource_timing: string
}

export type EndpointsSource = {
  application_events_base: string | null
  doppler: DopplerEndpointsSource
  pulsar: string
}

export type MeasurementsConfigSource = {
  geo?: GeoSource
}

export type FeatureFlagSettingsSource = {
  enabled: boolean
  rate: number
}

export type PlatformDefaultsSource = {
  hdx?: boolean
}

export type SessionConfigDefaultsSource = {
  platform?: PlatformDefaultsSource
}

export type SessionConfigJSONv2 = {
  bu_settings: SiteOwnerSettingsSource
  endpoints: EndpointsSource
  defaults?: SessionConfigDefaultsSource
  profiles: Record<string, PlatformProfileSource>
  platforms: PlatformSource[]
}

export type SessionMetadata = {
  browserMeta: BrowserMetadata
  clientVersion: ClientVersion
  sessionStartTimestamp: Date
  sessionUUID: string
}

export enum ConsoleOutputLevel {
  /**
   * Output using `console.debug` if permitted by page settings
   */
  debug,
  /**
   * Output using `console.error` if permitted by page settings
   */
  error,
  /**
   * Output using `console.info` if permitted by page settings
   */
  info,
  /**
   * Output using `console.warn` if permitted by page settings
   */
  warn,
}

export type OutputToConsoleFunc = (
  messageLogLevel: ConsoleOutputLevel,
  callback: () => string,
) => void
